import { DialogPanel } from '@headlessui/react';
import {
  AnchorButton,
  Button,
  Card,
  UntitledLoading,
  useToastContext,
} from '@payaca/ambrosia';
import {
  useEffectOnce,
  useFirstMountState,
  useKeyPressEvent,
} from '@payaca/hooks';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { AnimatePresence, motion, Variants } from 'motion/react';
import { FC, Fragment, useReducer } from 'react';
import type { LinkProps } from 'react-router-dom';
import ShadowDomRenderer from '../shadowDomRenderer/ShadowDomRenderer';
import LightBoxImage from './LightBoxImage';
import LightboxLink from './LightboxLink';
import LightboxMessage from './LightboxMessage';
import LightboxPdf from './LightboxPDF';
import LightboxVideo from './LightboxVideo';

export type TProps = {
  onClose: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  lightboxDetail:
    | {
        type: 'loading';
      }
    | {
        type: 'image';
        src: string;
        thumbnailSrc?: string;
        title: string;
        subtitle: React.ReactNode;
      }
    | {
        type: 'pdf';
        src: string;
        title: string;
        subtitle: React.ReactNode;
      }
    | {
        type: 'video';
        src: string;
        videoType: string;
        title: string;
        subtitle: React.ReactNode;
      }
    | {
        type: 'link';
        label: string;
        To?: LinkProps['to'];
        href?: string;
        onClick?: () => void;
        isProcessing?: boolean;
        title: string;
        subtitle: React.ReactNode;
      }
    | {
        type: 'message';
        children: React.ReactNode;
        title: string;
        subtitle: React.ReactNode;
      }
    | {
        type: 'shadowDOM';
        html: string;
        title: string;
        subtitle: React.ReactNode;
      };
};

const LightboxContent: FC<TProps> = (props) => {
  const { onPrev, onNext, onClose, lightboxDetail } = props;

  /**
   * state
   */
  const { pushInfo } = useToastContext();
  const isFirstMount = useFirstMountState();
  const [directionState, directionStateDispatch] = useReducer(
    lightboxContentReducer,
    {
      key: 0,
    }
  );

  useEffectOnce(() => {
    if (onNext || onPrev) {
      pushInfo('Use left and right arrow keys to navigate');
    }
  });

  const handleMoveToPrevious = () => {
    if (onPrev) {
      directionStateDispatch({
        type: 'moveToPrev',
      });
      onPrev();
    }
  };

  const handleMoveToNext = () => {
    if (onNext) {
      directionStateDispatch({
        type: 'moveToNext',
      });
      onNext();
    }
  };

  useKeyPressEvent('ArrowLeft', handleMoveToPrevious);
  useKeyPressEvent('ArrowRight', handleMoveToNext);

  const { key, direction } = directionState;

  return (
    <DialogPanel as={Fragment}>
      <motion.div
        layoutRoot
        initial={{
          opacity: 0,
        }}
        animate={{
          opacity: 1,
        }}
        exit={{
          opacity: 0,
        }}
        className="fixed z-lightbox inset-0 w-svw h-svh flex items-center p-4 gap-4 justify-center"
      >
        <Button
          size="sm"
          variant="ghost"
          colour="white"
          onClick={handleMoveToPrevious}
          disabled={!onPrev}
          className={!onPrev && !onNext ? 'hidden' : ''}
        >
          <UntitledIcon name="chevron-left" className="w-8 h-8" />
        </Button>

        <AnimatePresence mode="wait" custom={{ direction }}>
          <motion.div
            key={key}
            className={clstx(
              'max-h-full relative overflow-hidden mx-auto mb-[8.5rem]',
              (lightboxDetail.type === 'pdf' ||
                lightboxDetail.type === 'shadowDOM') &&
                'flex-1'
            )}
            variants={LightboxContentAnimationVariants}
            // not needed, because we are using custom prop in parent AnimatePresence
            custom={{ direction }}
            initial={isFirstMount ? 'initial' : 'enter'}
            animate="center"
            exit="exit"
            transition={{
              type: 'tween',
            }}
          >
            {lightboxDetail.type === 'loading' && (
              <UntitledLoading className="h-10 w-10 text-white" />
            )}
            {lightboxDetail.type === 'image' && (
              <LightBoxImage
                src={lightboxDetail.src}
                thumbnailSrc={lightboxDetail.thumbnailSrc}
                alt={lightboxDetail.title}
              />
            )}
            {lightboxDetail.type === 'video' && (
              <LightboxVideo
                src={lightboxDetail.src}
                type={lightboxDetail.videoType}
              />
            )}
            {lightboxDetail.type === 'pdf' && (
              <LightboxPdf src={lightboxDetail.src} />
            )}
            {lightboxDetail.type === 'link' && (
              <LightboxLink {...lightboxDetail} />
            )}
            {lightboxDetail.type === 'message' && (
              <LightboxMessage>{lightboxDetail.children}</LightboxMessage>
            )}
            {lightboxDetail.type === 'shadowDOM' && (
              <ShadowDomRenderer
                html={lightboxDetail.html}
                className="bg-white shadow-sm rounded-sm h-[calc((100svh-(var(--spacing-4)*2)-7rem))] overflow-auto"
              />
            )}
          </motion.div>
        </AnimatePresence>

        <Button
          size="sm"
          variant="ghost"
          colour="white"
          onClick={handleMoveToNext}
          disabled={!onNext}
          className={!onPrev && !onNext ? 'hidden' : ''}
        >
          <UntitledIcon name="chevron-right" className="w-8 h-8" />
        </Button>

        {lightboxDetail.type !== 'loading' && (
          <div className="fixed bottom-4 left-4 right-4 flex justify-center">
            <Card className="max-w-screen-sidebar-open w-full">
              <Card.Body className="flex flex-col items-center md:flex-row md:items-start justify-between gap-2 ">
                <div>
                  <h4>{lightboxDetail.title}</h4>

                  {lightboxDetail.subtitle && <p>{lightboxDetail.subtitle}</p>}
                </div>

                {'src' in lightboxDetail && (
                  <div className="flex items-center gap-3">
                    <AnchorButton
                      variant="link-inline"
                      linkColour="dark"
                      href={lightboxDetail.src}
                      openInNewTab
                    >
                      <UntitledIcon name="expand-05.3" className="h-4 w-4" />
                      Fullscreen
                    </AnchorButton>

                    <div className="h-4 w-px bg-black" />

                    <AnchorButton
                      variant="link-inline"
                      linkColour="dark"
                      href={lightboxDetail.src}
                      openInNewTab
                      download
                    >
                      <UntitledIcon name="download-01.3" className="h-4 w-4" />
                      Download
                    </AnchorButton>
                  </div>
                )}

                {lightboxDetail.type === 'shadowDOM' && (
                  <Button
                    variant="link-inline"
                    linkColour="dark"
                    onClick={() => {
                      const printWindow = window.open('', '_blank');
                      if (printWindow) {
                        printWindow.document.write(lightboxDetail.html);
                        printWindow.document.close();
                        printWindow.focus();
                        printWindow.addEventListener('afterprint', () => {
                          printWindow.close();
                        });
                        printWindow.print();
                      }
                    }}
                  >
                    Print
                  </Button>
                )}
              </Card.Body>
            </Card>
          </div>
        )}
        <Button
          className="absolute right-2 top-2 md:top-4 md:right-4 bg-gray-400/20 md:bg-transparent"
          variant="ghost"
          colour="white"
          onClick={onClose}
        >
          <UntitledIcon name="x-close" className="w-5 h-5 md:w-6 md:h-6" />
        </Button>
      </motion.div>
    </DialogPanel>
  );
};

const LightboxContentAnimationVariants: Variants = {
  initial: { opacity: 0 },
  enter: ({ direction }: { direction: Direction }) => ({
    opacity: 0,
    x: direction === 'next' ? '100%' : '-100%',
  }),
  center: () => ({ opacity: 1, x: '0%' }),
  exit: ({ direction }: { direction: Direction }) => ({
    opacity: 0,
    x: direction === 'next' ? '-100%' : '100%',
  }),
};

type Direction = 'next' | 'prev';

type LightboxReducerState = {
  key: number;
  direction?: Direction;
};

type LightboxReducerAction =
  | {
      type: 'moveToPrev';
    }
  | {
      type: 'moveToNext';
    };

const lightboxContentReducer = (
  state: LightboxReducerState,
  action: LightboxReducerAction
): LightboxReducerState => {
  switch (action.type) {
    case 'moveToPrev':
      return {
        key: state.key - 1,
        direction: 'prev',
      };
    case 'moveToNext':
      return {
        key: state.key + 1,
        direction: 'next',
      };
    default:
      return state;
  }
};

export default LightboxContent;
